import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import AdminHeader from '../components/AdminHeader';
import ConfirmModal from '../components/ConfirmModal';
import '../components/AdminLayout.css';
import './AdminReviews.css';

function AdminReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReviewId, setDeleteReviewId] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [ratingFilter, setRatingFilter] = useState(null); // 1-5 or null for all
  const reviewsPerPage = 6;

  const fetchReviews = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API}/api/products/admin/reviews`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) throw new Error('Failed to load reviews');
      const data = await res.json();
      setReviews(data.reviews || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReviews(); }, []);
  
  // Reset to page 1 when search text changes
  useEffect(() => { setCurrentPage(1); }, [search]);

  const filtered = useMemo(() => {
    // Text filtering
    const base = (() => {
      if (!search) return reviews;
      const q = search.toLowerCase();
      return reviews.filter(r =>
        (r.productName || '').toLowerCase().includes(q) ||
        (r.comment || '').toLowerCase().includes(q) ||
        (r.userEmail || '').toLowerCase().includes(q) ||
        (r.userName || '').toLowerCase().includes(q)
      );
    })();
    // Rating filter
    if (ratingFilter == null) return base;
    return base.filter(r => Number(r.rating) === ratingFilter);
  }, [reviews, search, ratingFilter]);

  // Reset to page 1 when rating filter changes
  useEffect(() => { setCurrentPage(1); }, [ratingFilter]);

  // Pagination calculations
  const totalPages = Math.ceil(filtered.length / reviewsPerPage) || 1;
  const startIndex = (currentPage - 1) * reviewsPerPage;
  const pageItems = filtered.slice(startIndex, startIndex + reviewsPerPage);

  const handleDelete = async (reviewId) => {
    setDeleteReviewId(reviewId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteReviewId) return;
    setDeleting(deleteReviewId);
    try {
      const res = await fetch(`${process.env.REACT_APP_API}/api/products/admin/reviews/${deleteReviewId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) {
        let msg = `Failed to delete review (status ${res.status})`;
        try {
          const ct = res.headers.get('content-type') || '';
          if (ct.includes('application/json')) {
            const data = await res.json();
            if (data?.message) msg = data.message;
            else if (data?.error) msg = `Failed to delete review: ${data.error}`;
          } else {
            const text = await res.text();
            if (text) msg = text;
          }
        } catch {}
        throw new Error(msg);
      }
      await fetchReviews();
    } catch (e) {
      setErrorMessage(e.message);
      setShowErrorModal(true);
    } finally {
      setDeleting(null);
      setDeleteReviewId(null);
    }
  };

  return (
    <div className="admin-reviews-page" aria-label="Admin Reviews Page">
      <div className="admin-topbar" />
      <div className="admin-header-wrap">
        <div className="admin-header-container">
          <AdminHeader />
        </div>
      </div>

      <main className="admin-content" role="main">
        <div className="page-title-row">
          <h1 className="page-title" aria-label="Product Reviews">Product Reviews</h1>
          <div className="page-stats" aria-live="polite">Total: {reviews.length}</div>
        </div>

        <div className="controls-row" role="region" aria-label="Review controls">
          <label className="visually-hidden" htmlFor="searchReviews">Search reviews</label>
          <input
            id="searchReviews"
            className="input"
            placeholder="Search by product, comment, user"
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label="Search reviews by product, comment, or user"
          />
          <div className="rating-filter" role="group" aria-label="Filter by rating">
            <button
              type="button"
              className={`rating-chip ${ratingFilter == null ? 'active' : ''}`}
              onClick={() => setRatingFilter(null)}
              aria-label="Show all ratings"
            >All</button>
            {[1,2,3,4,5].map(r => (
              <button
                type="button"
                key={r}
                className={`rating-chip ${ratingFilter === r ? 'active' : ''}`}
                onClick={() => setRatingFilter(prev => prev === r ? null : r)}
                aria-label={`Filter rating ${r} star${r>1?'s':''}`}
              >{r}★</button>
            ))}
          </div>
          <button
            className="btn btn-primary"
            onClick={fetchReviews}
            aria-label="Refresh reviews"
          >Refresh</button>
        </div>

        {error && <div className="alert" role="alert" aria-live="assertive">{error}</div>}

        {loading ? (
          <div className="loading" aria-live="polite">Loading reviews...</div>
        ) : filtered.length === 0 ? (
          <div className="empty" aria-live="polite">No reviews found.</div>
        ) : (
          <section className="cards" aria-label="Review list">
            {pageItems.map(r => (
              <motion.article
                key={r.reviewId}
                initial={{ opacity: 0, translateY: 8 }}
                animate={{ opacity: 1, translateY: 0 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
                className="card"
                aria-label={`Review card for ${r.productName}`}
              >
                <header className="card-header">
                  <h2 className="card-title" title={r.productName}>{r.productName}</h2>
                  <span className="rating" aria-label={`Rating ${r.rating} stars`}>{r.rating}★</span>
                </header>
                <p className="card-comment">{r.comment}</p>
                <div className="card-meta">
                  <span>
                    <span className="meta-label">User:</span> {r.userName || 'N/A'} {r.verified && <span className="verified" aria-label="Verified buyer">✔ Verified</span>}
                  </span>
                  {r.userEmail && <span><span className="meta-label">Email:</span> {r.userEmail}</span>}
                  <time dateTime={new Date(r.createdAt).toISOString()}>{new Date(r.createdAt).toLocaleString()}</time>
                </div>
                <footer className="card-actions">
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDelete(r.reviewId)}
                    disabled={deleting === r.reviewId}
                    aria-disabled={deleting === r.reviewId ? 'true' : 'false'}
                    aria-label={`Delete review by ${r.userName || 'user'} for ${r.productName}`}
                  >{deleting === r.reviewId ? 'Deleting…' : 'Delete'}</button>
                </footer>
              </motion.article>
            ))}
          </section>
        )}

        {/* Pagination */}
        {totalPages > 0 && (
          <nav className="pagination-container" role="navigation" aria-label="Reviews pagination">
            <div className="pagination-info">
              Showing {pageItems.length > 0 ? startIndex + 1 : 0}-{Math.min(currentPage * reviewsPerPage, filtered.length)} of {filtered.length} reviews
            </div>
            <div className="pagination-controls">
              <button
                className="page-btn"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                aria-label="Go to first page"
              >First</button>
              <button
                className="page-btn"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                aria-label="Go to previous page"
              >Previous</button>
              {[...Array(totalPages)].map((_, index) => {
                const pageNum = index + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                      onClick={() => setCurrentPage(pageNum)}
                      aria-label={`Go to page ${pageNum}`}
                      aria-current={currentPage === pageNum ? 'page' : undefined}
                    >{pageNum}</button>
                  );
                } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                  return <span key={pageNum} className="page-ellipsis">...</span>;
                }
                return null;
              })}
              <button
                className="page-btn"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                aria-label="Go to next page"
              >Next</button>
              <button
                className="page-btn"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                aria-label="Go to last page"
              >Last</button>
            </div>
          </nav>
        )}
      </main>

      <div className="admin-footer" />

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteReviewId(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Review"
        message="Are you sure you want to delete this review? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      />

      <ConfirmModal
        isOpen={showErrorModal}
        onClose={() => {
          setShowErrorModal(false);
          setErrorMessage('');
        }}
        onConfirm={() => {}}
        title="Error"
        message={errorMessage}
        confirmText="OK"
        cancelText=""
        type="error"
      />
    </div>
  );
}

export default AdminReviewsPage;
